/**
 * Everything the live preview needs to look like "the site with all of my
 * unpublished edits on it" — section edits AND record (card) edits together.
 *
 * Pages mode used to patch only section fields and Lists mode only the open
 * list's records, so switching between a section and a card made the other
 * one's edits vanish from the preview (they were saved, just not drawn).
 * Both workspaces now send both kinds, built here.
 */
import { LIST_KEYS, type ListKey } from '@vv/sections';
import type { RecordFieldsItem, SectionFieldsItem } from '~/components/PagePreview';
import { LIST_DEFS, recKey } from '~/lib/lists';
import type { ListsApi } from '~/lib/useLists';
import type { PageDoc, SectionDraft } from '~/lib/usePageDocs';

/**
 * Flatten a section object to `dot.path → scalar` for the live-preview
 * field patcher. Matches the site's data-f tagging convention. Objects with
 * a string `src` also emit their parent path (gallery imgs are tagged
 * `images.0`, not `images.0.src`).
 */
export function flattenScalars(
  obj: Record<string, unknown>,
  prefix = '',
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (k === 'type') continue;
    const p = prefix ? `${prefix}.${k}` : k;
    if (typeof v === 'string' || typeof v === 'number') {
      out[p] = v;
    } else if (Array.isArray(v)) {
      v.forEach((item, i) => {
        if (typeof item === 'string' || typeof item === 'number') {
          out[`${p}.${i}`] = item;
        } else if (item && typeof item === 'object') {
          Object.assign(out, flattenScalars(item as Record<string, unknown>, `${p}.${i}`));
          const src = (item as Record<string, unknown>).src;
          if (typeof src === 'string') out[`${p}.${i}`] = src;
        }
      });
    } else if (v && typeof v === 'object') {
      Object.assign(out, flattenScalars(v as Record<string, unknown>, p));
    }
  }
  return out;
}

/** The page's section drafts as preview patches, plus the visible-section
 * order (hidden sections left out, which hides them live). With no page
 * doc (a static page such as /privacy) there is no order at all — NOT an
 * empty one: the preview reads [] as "hide every section". */
export function sectionPatches(doc: PageDoc | undefined): {
  sectionFields: SectionFieldsItem[];
  sectionOrder: string[] | undefined;
} {
  if (!doc) return { sectionFields: [], sectionOrder: undefined };
  const deployed = doc.sections.filter(
    (s): s is SectionDraft & { originalId: string } => s.originalId !== null,
  );
  return {
    sectionFields: deployed.map((s) => ({ id: s.originalId, fields: flattenScalars(s.data) })),
    sectionOrder: deployed.filter((s) => s.data.hidden !== true).map((s) => s.originalId),
  };
}

/** Every LOADED list's records as preview patches, plus each list's shown
 * order. Lists not loaded yet send nothing (their cards stay as built). */
export function recordPatches(lists: Pick<ListsApi, 'records' | 'loadedRecord'>): {
  recordFields: RecordFieldsItem[];
  recordOrder: Array<{ list: ListKey; order: string[] }>;
} {
  const recordFields: RecordFieldsItem[] = [];
  const recordOrder: Array<{ list: ListKey; order: string[] }> = [];
  for (const list of LIST_KEYS) {
    const rows = lists.records[list];
    if (!rows) continue;
    const def = LIST_DEFS[list];
    for (const r of rows) {
      const fields = def.previewFields(r);
      // Only a CHANGED photo is re-pointed (see useLists.loadedRecord).
      const loaded = lists.loadedRecord(list, r.id);
      if (r.image_url && r.image_url !== loaded?.image_url) {
        fields.image_url = String(r.image_url);
      }
      recordFields.push({ rec: recKey(list, r.id), fields });
    }
    recordOrder.push({
      list,
      order: rows.filter((r) => r.is_active).map((r) => recKey(list, r.id)),
    });
  }
  return { recordFields, recordOrder };
}
